import { useEffect, useRef, useState } from 'react';
import { Package, Plus, Check, Loader2, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useVertical } from '../../context/VerticalContext';
import { reportError } from '../../lib/errors';

/**
 * The part field: type three letters instead of the whole thing.
 *
 * IT IS A TEXT BOX FIRST AND A DROPDOWN SECOND.
 *
 * That ordering is the whole design. A mechanic with a part in his hand and a
 * name we have never heard of must be able to type it and carry on — so this is
 * a plain <input> that happens to offer suggestions, not a <select> that has to
 * be satisfied. If the RPC 500s, the network is down, or the org has no
 * catalogue at all, the field still works exactly like the one it replaced.
 * Every failure path below ends in "the typed text stands".
 *
 * WHERE THE SUGGESTIONS COME FROM
 *
 * fn_search_parts merges two sources and says which is which:
 *   - this workshop's own saved parts, WITH their prices, ranked by how often
 *     and how recently they have actually been put on jobs;
 *   - a shared dictionary of common part names, no prices. Trade names
 *     ("drop link", "aux belt") are aliases that find the canonical part, so
 *     the list shows "Anti roll bar link" once, never a second spelling.
 * An empty box already lists this workshop's most-used parts, so it is useful
 * before the first keystroke.
 *
 * SAVING A NEW PART
 *
 * Offered inline, because the alternative is a trip to a settings screen that
 * nobody makes mid-job. Before saving, fn_suggest_similar_part is asked whether
 * this looks like something that already exists — "brake fuid" vs "brake
 * fluid" — and the answer is SHOWN, never acted on. The database refuses exact
 * duplicates after normalisation ("BRAKE fluid" == "brake fluid"); a typo is a
 * judgement, and judgements belong to the user.
 */

interface PartSuggestion {
    catalog_item_id: string | null;
    name: string;
    item_type: string | null;
    cost_price: number | null;
    retail_price: number | null;
    /** 'org' = this workshop's own saved part; 'catalog' = shared dictionary. */
    source: string;
    use_count: number;
    last_used_at: string | null;
}

interface SimilarPart {
    catalog_item_id: string | null;
    name: string;
    source: string;
    similarity: number;
}

interface Props {
    value: string;
    onChange: (value: string) => void;
    /**
     * Fired whenever a suggestion is picked. Prices are null unless the
     * suggestion was this org's own saved part — a dictionary entry knows the
     * name and nothing about what anybody charges for it.
     */
    onPick?: (part: { name: string; costPrice: number | null; retailPrice: number | null }) => void;
    /**
     * Fired when the field is left with text that was TYPED rather than picked.
     * A pick never fires it — that is what onPick is for — so a host that saves
     * on blur cannot race a pick and write the half-typed prefix over the name
     * the user just chose.
     */
    onBlur?: () => void;
    itemType?: 'part' | 'labour';
    className?: string;
    placeholder?: string;
}

export function PartTypeahead({ value, onChange, onPick, onBlur, itemType = 'part', className = '', placeholder }: Props) {
    const { activeOrgId } = useVertical();
    const [open, setOpen] = useState(false);
    const [results, setResults] = useState<PartSuggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const [saving, setSaving] = useState(false);
    const [savedNote, setSavedNote] = useState<string | null>(null);
    const [similar, setSimilar] = useState<SimilarPart[] | null>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    // Set the instant a pointer goes down on a SUGGESTION, so the blur that
    // immediately follows is recognised as "the user is picking", not "the user
    // has finished typing". Checking relatedTarget instead would be wrong on
    // Safari, where a button does not take focus on mousedown.
    //
    // It is deliberately NOT set for "Save … to my parts". That button writes to
    // inventory_catalog and calls neither onChange nor onPick, so suppressing
    // the blur there would strand the typed description in the host's local
    // state: it looked saved, and disappeared on the next refresh. Only the
    // paths that hand the host a value get to swallow its blur.
    const pickingRef = useRef(false);

    // Debounced, because this fires on every keypress in a workshop on a phone
    // over a patchy connection. 200ms is below the threshold where the list
    // feels laggy and well above one-request-per-character.
    useEffect(() => {
        if (!open || !activeOrgId) return;
        let cancelled = false;
        const t = setTimeout(async () => {
            setLoading(true);
            const { data, error } = await supabase.rpc('fn_search_parts', {
                p_org_id: activeOrgId,
                p_query: value ?? '',
                p_limit: 8,
            });
            if (cancelled) return;
            // A failed lookup is not a failed field. Report it and show nothing;
            // whatever the user typed is still their line item.
            if (error) reportError(error, 'part-typeahead.fn_search_parts');
            setResults((data as PartSuggestion[] | null) ?? []);
            setHighlight(0);
            setLoading(false);
        }, 200);
        return () => { cancelled = true; clearTimeout(t); };
    }, [value, open, activeOrgId]);

    // Close on an outside tap. Pointerdown rather than click so it fires before
    // the input's blur on touch.
    useEffect(() => {
        const onDown = (e: PointerEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('pointerdown', onDown);
        return () => document.removeEventListener('pointerdown', onDown);
    }, []);

    const pick = (s: PartSuggestion) => {
        onChange(s.name);
        // Every pick is reported, so a host that persists on pick does not lose
        // a dictionary name. Only an org's own part carries prices, though — a
        // dictionary entry sends nulls and leaves the money alone rather than
        // zeroing it.
        const own = s.source === 'org';
        onPick?.({
            name: s.name,
            costPrice: own ? s.cost_price : null,
            retailPrice: own ? s.retail_price : null,
        });
        pickingRef.current = false;
        setOpen(false);
        setSimilar(null);
    };

    const typed = (value ?? '').trim();
    const alreadyListed = results.some(
        (r) => r.name.toLowerCase().replace(/\s+/g, ' ') === typed.toLowerCase().replace(/\s+/g, ' ')
    );
    const canSaveNew = typed.length >= 2 && !alreadyListed && !!activeOrgId;

    const saveNew = async () => {
        if (!activeOrgId || !typed) return;
        setSaving(true);
        setSimilar(null);

        // Ask BEFORE writing, and only ask — the answer is shown to the user.
        const { data: near, error: nearError } = await supabase.rpc('fn_suggest_similar_part', {
            p_org_id: activeOrgId,
            p_name: typed,
        });
        if (nearError) reportError(nearError, 'part-typeahead.fn_suggest_similar_part');

        const matches = (near as SimilarPart[] | null) ?? [];
        if (matches.length > 0) {
            setSimilar(matches);
            setSaving(false);
            return; // The user decides. Nothing has been written.
        }
        await commitNew();
    };

    const commitNew = async () => {
        if (!activeOrgId || !typed) return;
        setSaving(true);
        setSimilar(null);
        const { error } = await supabase
            .from('inventory_catalog')
            .insert({ org_id: activeOrgId, name: typed, type: itemType });
        setSaving(false);
        if (error) {
            // 23505 is the per-org unique index on the normalised name: the part
            // already exists under a different capitalisation. That is the
            // constraint doing its job, not a fault worth reporting.
            if (error.code === '23505') {
                setSavedNote('Already in your parts');
            } else {
                reportError(error, 'part-typeahead.inventory_catalog-insert');
                setSavedNote('Could not save — the line is still fine');
            }
        } else {
            setSavedNote('Saved to your parts');
        }
        setTimeout(() => setSavedNote(null), 2500);
        setOpen(false);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (!open) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
        else if (e.key === 'Enter' && results[highlight]) {
            // Only intercept Enter when a suggestion is actually highlighted, so
            // Enter on freely typed text still behaves like a normal field.
            e.preventDefault();
            e.stopPropagation();
            pick(results[highlight]);
        } else if (e.key === 'Escape') { setOpen(false); }
    };

    return (
        <div ref={wrapRef} className={`relative min-w-0 ${className}`}>
            <input
                type="text"
                value={value}
                onChange={(e) => { onChange(e.target.value); setOpen(true); setSimilar(null); }}
                onFocus={() => setOpen(true)}
                onBlur={() => {
                    if (pickingRef.current) { pickingRef.current = false; return; }
                    onBlur?.();
                }}
                onKeyDown={onKeyDown}
                placeholder={placeholder ?? 'Start typing a part…'}
                role="combobox"
                aria-expanded={open}
                aria-autocomplete="list"
                className="w-full bg-canvas sm:bg-transparent border border-line sm:border-transparent rounded-lg text-fg text-sm outline-none focus:border-gold px-2 py-2 sm:py-1 min-h-[44px] sm:min-h-0 min-w-0"
            />

            {savedNote && (
                <p className="mt-1 text-fd-3xs text-fg-3">{savedNote}</p>
            )}

            {open && (results.length > 0 || canSaveNew || loading) && (
                <div className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-lg border border-line bg-canvas shadow-2xl">
                    {loading && results.length === 0 && (
                        <p className="px-3 py-2 text-fd-3xs text-fg-3 flex items-center gap-2">
                            <Loader2 className="w-3 h-3 animate-spin" /> Searching…
                        </p>
                    )}

                    {results.map((s, i) => (
                        <button
                            key={`${s.source}-${s.catalog_item_id ?? s.name}`}
                            type="button"
                            onPointerDown={() => { pickingRef.current = true; }}
                            onClick={() => pick(s)}
                            onMouseEnter={() => setHighlight(i)}
                            className={`w-full text-left px-3 py-2 min-h-[44px] flex items-center gap-2 border-b border-line last:border-b-0 ${
                                i === highlight ? 'bg-card' : ''
                            }`}
                        >
                            <Package className={`w-3.5 h-3.5 shrink-0 ${s.source === 'org' ? 'text-gold' : 'text-fg-3'}`} />
                            <span className="flex-1 min-w-0 text-sm text-fg truncate">{s.name}</span>
                            {/* Their own price, when it is their own part. */}
                            {s.source === 'org' && s.retail_price != null && (
                                <span className="shrink-0 text-fd-3xs font-mono text-fg-3">
                                    £{Number(s.retail_price).toFixed(2)}
                                </span>
                            )}
                            {s.use_count > 0 && (
                                <span className="shrink-0 text-fd-3xs text-fg-3">
                                    ×{s.use_count}
                                </span>
                            )}
                        </button>
                    ))}

                    {canSaveNew && !similar && (
                        <button
                            type="button"
                            onClick={saveNew}
                            disabled={saving}
                            className="w-full text-left px-3 py-2 min-h-[44px] flex items-center gap-2 text-gold disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                            <span className="text-sm font-bold truncate">Save &ldquo;{typed}&rdquo; to my parts</span>
                        </button>
                    )}

                    {/* Shown, never acted on. The user picks. */}
                    {similar && (
                        <div className="p-3 border-t border-line bg-card">
                            <p className="flex items-center gap-1.5 text-fd-3xs font-bold uppercase tracking-wider text-gold mb-2">
                                <AlertTriangle className="w-3 h-3" /> Did you mean?
                            </p>
                            <div className="space-y-1">
                                {similar.map((m) => (
                                    <button
                                        key={`${m.source}-${m.name}`}
                                        type="button"
                                        onPointerDown={() => { pickingRef.current = true; }}
                                        onClick={() => {
                                            onChange(m.name);
                                            onPick?.({ name: m.name, costPrice: null, retailPrice: null });
                                            pickingRef.current = false;
                                            setSimilar(null);
                                            setOpen(false);
                                        }}
                                        className="w-full text-left px-2 py-2 min-h-[44px] rounded flex items-center gap-2 hover:bg-card"
                                    >
                                        <Check className="w-3.5 h-3.5 text-success shrink-0" />
                                        <span className="text-sm text-fg truncate">{m.name}</span>
                                    </button>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={commitNew}
                                disabled={saving}
                                className="mt-2 w-full min-h-[44px] text-fd-3xs font-bold text-fg-3 hover:text-fg disabled:opacity-50"
                            >
                                No — save &ldquo;{typed}&rdquo; as a new part
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
